import Core                  from "@/core/Core";
import NumberUtils           from '@/core/utils/NumberUtils';
import CurrenciesService     from '@/domains/Currencies/CurrenciesService';
import DateSimple            from "@/core/types/DateSimple";
import Currency              from '@/domains/Currencies/Currency';
import Decimal               from "decimal.js";

import type DeliveryType     from "@/domains/DeliveryTypes/DeliveryType";
import type Price            from "@/core/types/Price";
import type Sum              from "@/core/types/Sum";
import type VatRate          from "@/core/types/VatRate";
import type Multiplier       from "@/core/types/Multiplier";
import ApplicationError from "@/core/misc/ApplicationError";
import {ErrorSeverity} from "@/core/misc/ErrorSeverity";


class MoneyUtils {

  /**
   * Ядро расчёта суммы НДС в Decimal — единственная формула на весь фронт.
   * Принимает value денежного Numeric (Sum/Price), возвращает Decimal.
   * Оборачивают результат в свой тип прокси Sum.getVat / Price.getVat.
   * Ставка не задана (null/undefined) или неположительная — НДС нулевой:
   * функция вызывается на каждое изменение формы, включая незаполненную.
   * @param value - значение суммы/цены (Decimal из Numeric)
   * @param isVatIncluded - включён ли НДС в значение
   * @param vatRate - ставка НДС в процентах (число, value из VatRate,
   *                  либо null/undefined, если ставка не задана)
   */
  static getVat(value: Decimal, isVatIncluded: boolean | null, vatRate: Decimal.Value | null | undefined): Decimal {
    if (vatRate === null || vatRate === undefined || !new Decimal(vatRate).greaterThan(0)) {
      // Ставка не задана либо 0 или отрицательная — НДС нулевой.
      return new Decimal(0);
    } else if (isVatIncluded) {
      // НДС включён: value / (100 + rate) * rate
      const rate = new Decimal(vatRate);

      return value.div(rate.plus(100)).times(rate);
    } else {
      // НДС сверху: value * (rate / 100)
      const rate = new Decimal(vatRate);

      return value.times(rate.div(100));
    }
  }

  /**
   * Ядро расчёта полной величины (с НДС) в Decimal — единственная формула на весь фронт.
   * Принимает value денежного Numeric (Sum/Price), возвращает Decimal.
   * Оборачивают результат в свой тип прокси Sum.getFull / Price.getFull.
   * Ставка не задана (null/undefined) — начислять нечего, возвращается value
   * как есть: функция вызывается на каждое изменение формы, включая
   * незаполненную, и new Decimal(undefined) здесь падал бы с DecimalError.
   *
   * @param value - значение суммы/цены (Decimal из Numeric)
   * @param isVatIncluded - включён ли НДС в значение
   * @param vatRate - ставка НДС в процентах (число, value из VatRate,
   *                  либо null/undefined, если ставка не задана)
   */
  static getFull(value: Decimal, isVatIncluded: boolean | null, vatRate: Decimal.Value | null | undefined): Decimal {
    if (Core.utils.isNullOrUndefined(vatRate) === true) {
      // Ставка не задана — начислять нечего
      return value;
    } else if (isVatIncluded) {
      // НДС включён: величина уже полная
      return value;
    } else {
      const rate = new Decimal(vatRate);
      // НДС сверху: value * (1 + rate / 100)
      return value.times(rate.div(100).plus(1));
    }
  }

  /**
   * Ядро расчёта величины БЕЗ НДС в Decimal — операция, обратная getFull.
   * Принимает value денежного Numeric (Sum/Price), возвращает Decimal.
   * Оборачивают результат в свой тип прокси Sum.getNet / Price.getNet.
   *
   * @param value - значение суммы/цены (Decimal из Numeric)
   * @param isVatIncluded - включён ли НДС в значение
   * @param vatRate - ставка НДС в процентах (число или value из VatRate)
   */
  static getNet(value: Decimal, isVatIncluded: boolean, vatRate: Decimal.Value): Decimal {
    const rate = new Decimal(vatRate);

    if (isVatIncluded) {
      // НДС включён: полная величина и есть чистая
      return value;
    } else {
      // НДС сверху: value / (1 + rate / 100)
      return value.dividedBy(rate.div(100).plus(1));
    }
  }

  /**
   * Приводит валюту к её коду. Принимает и код, и объект Currency: в моделях
   * валюта лежит по-разному, а конвертерам и сервису курсов нужен только код.
   *
   * @param currency - объект валюты либо её код
   *
   * @returns Код валюты
   */
  static resolveCurrencyCode(currency: Currency | string): string {
    if (typeof currency === 'string') {
      return currency;
    }
    else {
      return currency.code;
    }
  }

  /**
   * Конвертирует денежную величину из одной валюты в другую через базовую
   * валюту партнёра. Полиморфна по типу: Sum → Sum, Price → Price — результат
   * рождается тем же типом, что и вход, и округляется до его scale
   * конструктором (round10 внутри больше не нужен).
   *
   * Валюта источника/цели передаётся отдельно — сам Numeric валюту не несёт.
   * Принимается и код, и объект Currency, как в convertSync. Базовая валюта
   * партнёра — промежуточное звено: from → base → to.
   *
   * @param amount   - конвертируемая величина (Sum или Price)
   * @param from     - исходная валюта либо её код
   * @param to       - целевая валюта либо её код
   * @param dateRate - дата курса
   */
  static async convert<T extends Sum | Price>(
    amount: T,
    from: Currency | string,
    to: Currency | string,
    dateRate: Date | DateSimple,
  ): Promise<T> {
    const fromCurrencyCode = this.resolveCurrencyCode(from);
    const toCurrencyCode   = this.resolveCurrencyCode(to);

    // Валюты одинаковые — конвертация не нужна, величина уже в целевой валюте
    if (fromCurrencyCode === toCurrencyCode) {
      return amount;
    }

    const date = this.resolveRateDate(dateRate, fromCurrencyCode, toCurrencyCode);
    /** Валюта, которую использует партнёр — промежуточное звено конвертации */
    const baseCurrencyCode = Core.currentPartner!.country.localCurrency.code;

    /** Курсы независимы друг от друга — берём их одним заходом, а не
     *  последовательно: второй запрос не ждёт ответа первого. */
    const [sourceToBaseRate, targetToBaseRate] = await Promise.all([
      // Курс исходной валюты к базовой (сколько базовой в 1 единице исходной)
      CurrenciesService.getRate(fromCurrencyCode, baseCurrencyCode, date),
      // Курс целевой валюты к базовой (сколько базовой в 1 единице целевой)
      CurrenciesService.getRate(toCurrencyCode, baseCurrencyCode, date),
    ]);

    /** Фабрика типа входа — результат рождается тем же типом (Sum/Price),
     * scale-округление вшито в конструктор через fromOperation. */
    const factory = (amount.constructor as typeof Sum | typeof Price);

    /** Проход from → base → to одной формулой. Отдельных веток на «курс
     *  равен 1» нет сознательно: times(1) и dividedBy(1) в decimal.js точны,
     *  результат тот же, а признак «валюта базовая» по значению курса
     *  выводить нельзя — единице может быть равен и настоящий курс. */
    const converted: Decimal = amount.value.times(sourceToBaseRate.value).dividedBy(targetToBaseRate.value);

    return factory.fromOperation(converted) as T;
  }

  /**
   * Приводит дату курса к DateSimple и ручается за её пригодность.
   * Единственная нормализация даты в классе: convert, convertSync и
   * getRateFallbackMark ходят сюда, чтобы ключ курса у всех трёх строился
   * одинаково.
   *
   * Пустота проверяется ДО конструирования, а не валидностью результата:
   * new Date(null) — это new Date(0), то есть 01.01.1970. Дата выходит
   * валидная, isNaN её не отсекает, курс спрашивается за эпоху, и сервер
   * честно отвечает «курса нет» — на экране это неотличимо от настоящего
   * отсутствия курса. undefined даёт Invalid Date и ключ вида "NaN-NaN-NaN".
   * Оба случая — незаполненное поле модели, а не состояние курсов, и
   * заглушкой показываться не должны.
   *
   * Тип шире объявленного в convertSync сознательно: сигнатура пустоту уже
   * запрещает, но приходит она именно оттуда, где TS о ней не знает —
   * из необязательного поля модели, объявленного обязательным.
   *
   * Бросает, а не возвращает заглушку, в том числе на пути отображения:
   * дефект относится к модели, а не к ячейке, и тот же null в этот момент
   * едет во все прочие потребители поля.
   *
   * @param date - дата курса
   * @param from - код исходной валюты (только для контекста ошибки)
   * @param to   - код целевой валюты (только для контекста ошибки)
   */
  private static resolveRateDate(
    date: Date | DateSimple | null | undefined,
    from: string,
    to: string,
  ): DateSimple {
    if (date === null || date === undefined) {
      throw new ApplicationError(
        `Не задана дата курса (${from} → ${to}) — конвертировать нельзя`,
        0,
        ErrorSeverity.Error,
        { from: from, to: to, date: String(date) },
      );
    }

    let result: DateSimple;

    if (date instanceof DateSimple) {
      result = date;
    }
    else {
      result = new DateSimple(date);
    }

    if (DateSimple.isDate(result) === false) {
      throw new ApplicationError(
        `Некорректная дата курса (${from} → ${to}) — конвертировать нельзя`,
        0,
        ErrorSeverity.Error,
        { from: from, to: to, date: String(date) },
      );
    }
    else {
      return result;
    }
  }

  /** Заглушка на месте числа, пока курсы ещё грузятся. */
  static readonly RATE_LOADING_MARK = '…';

  /** Заглушка на месте числа, когда курса на эту дату не существует.
   *  ✕ (U+2715), а не × (U+00D7): в числовой колонке знак умножения читается
   *  как часть выражения. //TODO облагородить — заменить на иконку. */
  static readonly RATE_MISSING_MARK = '✕';

  /**
   * Заглушка вместо значения, когда курс недоступен, или null, если курс
   * есть и надо печатать число.
   *
   * Различает два неустранимо разных случая. Курсы хранятся не за все
   * календарные дни и не за всю историю по всем валютам: для валюты,
   * добавленной позже даты документа, курса не появится никогда. Поэтому
   * «ещё едет» и «не существует» показываются по-разному — иначе
   * пользователь смотрит на пустую ячейку и не знает, ждать ему или нет.
   *
   * @param from - валюта, в которой выражена величина, либо её код
   * @param to   - валюта, в которую конвертируем, либо её код
   * @param date - дата курса
   */
  static getRateFallbackMark(from: Currency | string, to: Currency | string, date: Date | DateSimple): string | null {
    const fromCurrencyCode = this.resolveCurrencyCode(from);
    const toCurrencyCode   = this.resolveCurrencyCode(to);

    // Валюты одинаковые — курс в конвертации не участвует, заглушка не нужна
    if (fromCurrencyCode === toCurrencyCode) {
      return null;
    }

    /** Дата нормализуется тем же resolveRateDate, что и в convertSync:
     *  ключ курса у обеих функций обязан быть один, иначе заглушка и
     *  значение смотрят в разные ячейки кэша. */
    const dateObj = this.resolveRateDate(date, fromCurrencyCode, toCurrencyCode);

    const sourceToBaseRate = CurrenciesService.getRateCached(fromCurrencyCode, dateObj);
    const targetToBaseRate = CurrenciesService.getRateCached(toCurrencyCode, dateObj);

    if (sourceToBaseRate !== null && targetToBaseRate !== null) {
      return null;
    }
    else if (CurrenciesService.isRateMissing(fromCurrencyCode, dateObj) || CurrenciesService.isRateMissing(toCurrencyCode, dateObj)) {
      // Запрос по паре отработал, курса нет — ждать нечего
      return this.RATE_MISSING_MARK;
    }
    else {
      /** Курса нет и его не спрашивали — регистрируем потребность.
       *  Побочный эффект в отрисовке сознателен: он даёт автономность,
       *  курсы доберутся сами без участия загрузчика страницы. Запросы
       *  копятся за проход и уходят одним, повторы отсекаются. */
      CurrenciesService.requestRate(fromCurrencyCode, dateObj);
      CurrenciesService.requestRate(toCurrencyCode, dateObj);

      return this.RATE_LOADING_MARK;
    }
  }

  /**
   * Приводит валюту к объекту Currency. Принимает и код, и сам объект:
   * в моделях валюта лежит по-разному — где-то гидрированным объектом,
   * где-то только кодом, и заставлять вызывающего доставать объект ради
   * форматирования незачем.
   *
   * null на входе — осмысленное значение «без валюты», проходит насквозь.
   *
   * @param currency - объект валюты, её код или null
   *
   * @returns Валюта, либо null — если передан null или код неизвестен
   */
  static resolveCurrency(currency: Currency | string | null): Currency | null {
    if (currency === null) {
      return null;
    }
    else if (typeof currency === 'string') {
      return CurrenciesService.currencies.get(currency) ?? null;
    }
    else {
      return currency;
    }
  }

  /**
   * Валюта отображения. Прокси на CurrenciesService — чтобы денежные типы
   * (Price, Sum) не импортировали сервис валют напрямую: они уже тянут
   * MoneyUtils, а лишнее ребро Price → CurrenciesService → Core рискует
   * замкнуть цикл на этапе инициализации.
   */
  static get displayCurrency(): Currency | null {
    return CurrenciesService.displayCurrency;
  }

  /**
   * Синхронная конвертация для отображения. В отличие от convert() не ждёт
   * сервер: курс берётся из реактивного кэша CurrenciesService, поэтому
   * результат можно вернуть прямо в шаблон.
   *
   * Полиморфна по типу: Sum → Sum, Price → Price — результат рождается тем
   * же типом, что и вход, и округляется до его scale конструктором.
   * Логика прохода from → base → to повторяет convert() один в один.
   *
   * Курс обязан быть в кэше к моменту вызова. Отсутствие курса — дефект
   * загрузчика данных, а не штатная ситуация: пустое значение возвращается
   * только чтобы не уронить рендер.
   *
   * @param amount - конвертируемая величина (Sum или Price)
   * @param from   - валюта, в которой выражена величина, либо её код
   * @param to     - валюта, в которую конвертируем, либо её код
   * @param date   - дата курса
   */
  static convertSync<T extends Sum | Price>(
    amount: T,
    from:   Currency | string,
    to:     Currency | string,
    date:   Date | DateSimple,
  ): T {
    const fromCurrencyCode = this.resolveCurrencyCode(from);
    const toCurrencyCode   = this.resolveCurrencyCode(to);

    // Валюты одинаковые — конвертация не нужна, величина уже в целевой валюте
    if (fromCurrencyCode === toCurrencyCode) {
      return amount;
    } else {
      const dateObj = this.resolveRateDate(date, fromCurrencyCode, toCurrencyCode);

      // Курс исходной валюты к базовой (сколько базовой в 1 единице исходной)
      const sourceToBaseRate = CurrenciesService.getRateCached(fromCurrencyCode, dateObj);

      // Курс целевой валюты к базовой (сколько базовой в 1 единице целевой)
      const targetToBaseRate = CurrenciesService.getRateCached(toCurrencyCode, dateObj);

      /** Фабрика типа входа — результат рождается тем же типом (Sum/Price),
       * scale-округление вшито в конструктор через fromOperation. */
      const factory = (amount.constructor as typeof Sum | typeof Price);

      if (sourceToBaseRate === null || targetToBaseRate === null) {
        /** Курса нет. Две причины, неразличимые здесь: предзагрузка не
         * завершилась, либо курса на эту дату не существует (курсы
         * хранятся не за все дни и не за всю историю по всем валютам).
         * Отличить их может вызывающий через
         * CurrenciesService.isRateDateLoaded — решение, показывать
         * загрузку или прочерк, принимает компонент, не тип.
         * Не логируем: иначе на каждую ячейку грида придётся по записи. */
        return factory.fromEmpty() as T;
      } else {
        /** Проход from → base → to одной формулой — ровно как в convert().
         *  Отдельных веток на «курс равен 1» нет: times(1) и dividedBy(1)
         *  в decimal.js точны, результат тот же, а признак «валюта
         *  базовая» по значению курса выводить нельзя — единице может
         *  быть равен и настоящий курс. */
        const converted: Decimal = amount.value.times(sourceToBaseRate.value).dividedBy(targetToBaseRate.value);

        return factory.fromOperation(converted) as T;
      }
    }
  }

  static getSymbolByCurrency(currencyCode: string): string;
  static getSymbolByCurrency(currency: Currency): string;
  static getSymbolByCurrency(currencyOrCode: string | Currency): string {
    if (currencyOrCode instanceof Currency) {
      return currencyOrCode.symbol;
    }

    const currency = CurrenciesService.currencies.get(currencyOrCode);

    if (!currency) {
      Core.error(`Передаваемая валюта ${currencyOrCode} не поддерживается`);
      throw TypeError(`Передаваемая валюта ${currencyOrCode} не поддерживается`);
    }

    return currency.symbol;
  }

  /**
   * Валюта по коду. Возвращает null для неизвестного кода: Map.get даёт
   * undefined, а объявленный тип Currency этого не отражал, и место вызова
   * получало «валюту», которой нет.
   */
  static getCurrencyByCode(currencyCode: string): Currency | null {
    const currency = CurrenciesService.currencies.get(currencyCode);

    if (currency !== undefined) {
      return currency;
    } else {
      return null;
    }
  }

  /**
   * Полная цена покупателя из полной цены продавца, в той же валюте.
   *
   * Все шаги идут в Decimal, промежуточных Price нет: округление цены — это
   * материализация, а внутри формулы цена ещё не родилась. Единственная
   * материализация — в return. Округление сумм, предписанное НК (сумма →
   * округление → НДС из округлённой суммы), живёт уровнем выше, на Sum,
   * и этой функции не касается.
   *
   * Цена продавца и признак «НДС в цене продавца» из параметров ушли: они
   * были нужны только проходу через цену без НДС. Переход со ставки продавца
   * на ставку покупателя — один множитель от полной цены продавца,
   * см. getVatRebaseMultiplier.
   *
   * @param priceFullSeller      - полная цена продавца (с НДС)
   * @param vatRateSeller        - ставка НДС продавца (null — не облагается)
   * @param vatRateBuyer         - ставка НДС покупателя
   * @param additionalPayment    - наценка множителем (1 — без наценки)
   * @param deliveryPricePerUnit - стоимость доставки за единицу (при доставке платформой)
   * @param deliveryType         - тип доставки
   */
  static getPriceFullBuyerInGivenCurrency(
    priceFullSeller: Price,
    vatRateSeller: VatRate | null,
    vatRateBuyer: VatRate | null,
    additionalPayment: Multiplier,
    deliveryPricePerUnit: Price | null,
    deliveryType: DeliveryType,
  ): Price {
    const rateBuyer: VatRate = this.validateVatRateBuyer(vatRateBuyer);

    let result: Decimal = priceFullSeller.value.times(this.getVatRebaseMultiplier(vatRateSeller, rateBuyer));

    /** Множитель 1 — наценки нет, умножение было бы no-op. Скидку (< 1)
     *  прежний number-расчёт тоже не применял: условие было «> 0 процентов». */
    if (additionalPayment.greaterThan(1)) {
      result = result.times(additionalPayment.value);
    }

    // добавляем стоимость доставки за единицу, если тип доставки платформой
    if (deliveryType.id === 'byETP' && deliveryPricePerUnit !== null) {
      result = result.plus(deliveryPricePerUnit.value);
    }

    return this.toPrice(priceFullSeller, result);
  }

  /**
   * Множитель перехода с базы НДС продавца на базу НДС покупателя:
   * (100 + ставка покупателя) / (100 + ставка продавца).
   *
   * Заменяет прежний проход через цену без НДС. Тот проход давал те же числа,
   * но ценой двух материализаций Price вместо нуля и требовал знать цену
   * продавца и признак «НДС в цене продавца».
   *
   * Ставка продавца не ниже ставки покупателя — множитель 1: платформа цену
   * продавца не снижает. Поведение прежнего расчёта сохранено, там это была
   * ветка «если ставка НДС одинаковая, то за основу берем цену поставщика».
   *
   * Ставка продавца null — продавец не облагается, считаем ставку нулевой.
   * Ставка ПУСТАЯ (форма недозаполнена) — множитель NaN, и он доезжает до
   * результата: пустая цена печатается пустой строкой. Ошибку тут бросать
   * нельзя, расчёт дёргается на каждое изменение формы. Отдельная ветка
   * нужна потому, что сравнение с NaN даёт false и без неё пустая ставка
   * ушла бы в множитель 1 — то есть в правдоподобную цену без НДС.
   *
   * @param vatRateSeller - ставка НДС продавца (null — не облагается)
   * @param vatRateBuyer  - ставка НДС покупателя
   */
  private static getVatRebaseMultiplier(vatRateSeller: VatRate | null, vatRateBuyer: VatRate): Decimal {
    if (vatRateBuyer.isEmpty() === true || (vatRateSeller !== null && vatRateSeller.isEmpty() === true)) {
      return new Decimal(NaN);
    }

    let rateSeller: Decimal;

    if (vatRateSeller === null) {
      rateSeller = new Decimal(0);
    }
    else {
      rateSeller = vatRateSeller.value;
    }

    const rateBuyer: Decimal = vatRateBuyer.value;

    if (rateSeller.lessThan(rateBuyer)) {
      return rateBuyer.plus(100).dividedBy(rateSeller.plus(100));
    }
    else {
      return new Decimal(1);
    }
  }

  /**
   * Материализует посчитанный Decimal в Price — единственная точка округления
   * в денежных расчётах этого класса.
   *
   * Price импортирован типом: значением он замкнул бы цикл
   * Price → Core → MoneyUtils → Price. Поэтому фабрика берётся с конструктора
   * образца — тем же приёмом, что в convert и convertSync.
   *
   * @param sample - любая цена из расчёта, нужна только ради конструктора
   * @param value  - посчитанное значение
   */
  private static toPrice(sample: Price, value: Decimal): Price {
    const factory = sample.constructor as typeof Price;

    return factory.fromOperation(value);
  }

  /**
   * Полная цена продавца (с НДС) из его цены.
   * Не плательщик НДС — цена и есть полная. Остальное решает Price.getFull:
   * правило «НДС внутри — цена уже полная, НДС сверху — начислить» держится
   * там в единственном экземпляре, здесь оно не дублируется.
   *
   * @param price         - цена продавца
   * @param isVatIncluded - включён ли НДС в цену
   * @param isVatPayer    - платит ли продавец НДС
   * @param vatRate       - ставка НДС продавца
   */
  static getPriceFullSellerInGivenCurrency(
    price: Price,
    isVatIncluded: boolean,
    isVatPayer: boolean,
    vatRate: VatRate,
  ): Price {
    if (isVatPayer !== true) {
      return price;
    }
    else {
      return price.getFull(isVatIncluded, vatRate);
    }
  }

  /**
   * Цена продавца из полной цены покупателя — операция, обратная
   * getPriceFullBuyerInGivenCurrency: те же шаги в обратном порядке.
   *
   * Проход в Decimal, одна материализация в return. Прежний вариант делал
   * четыре и сам предупреждал, что с прямым расчётом не сходится; теперь
   * расхождение — только финальное округление до scale цены.
   *
   * ИЗМЕНЕНИЕ ПОВЕДЕНИЯ. Прежний расчёт снимал НДС покупателя всегда, а НДС
   * продавца возвращал только при isVatIncludedSeller, и наценку делил
   * безусловно. При ставке продавца ВЫШЕ ставки покупателя и при множителе
   * наценки 1 и меньше это не было обратной операцией: прямой расчёт в этих
   * случаях цену не трогает. Теперь снимается ровно то, что начислено — тем
   * же getVatRebaseMultiplier и под тем же условием на наценку.
   *
   * @param priceFullBuyer       - полная цена покупателя
   * @param vatRateSeller        - ставка НДС продавца (null — не облагается)
   * @param vatRateBuyer         - ставка НДС покупателя
   * @param isVatIncludedSeller  - включён ли НДС в цену продавца
   * @param additionalPayment    - наценка множителем (1 — без наценки)
   * @param deliveryPricePerUnit - стоимость доставки за единицу
   * @param deliveryType         - тип доставки
   */
  static calculatePriceSellerFromPriceFullBuyer(
    priceFullBuyer: Price,
    vatRateSeller: VatRate | null,
    vatRateBuyer: VatRate,
    isVatIncludedSeller: boolean,
    additionalPayment: Multiplier,
    deliveryPricePerUnit: Price | null,
    deliveryType: DeliveryType,
  ): Price {
    MoneyUtils.validateDeliveryPricePerUnit(deliveryPricePerUnit, deliveryType);

    let result: Decimal = priceFullBuyer.value;

    /** Первым делом, если доставка платформой, вычитаем стоимость доставки */
    if (deliveryType.id === 'byETP' && deliveryPricePerUnit !== null) {
      result = result.minus(deliveryPricePerUnit.value);
    }

    /** Снимаем наценку. Условие зеркалит прямой расчёт: множитель 1 и меньше
     *  там не применялся, значит и снимать нечего. */
    if (additionalPayment.greaterThan(1)) {
      result = result.dividedBy(additionalPayment.value);
    }

    /** Возвращаемся с базы НДС покупателя на базу НДС продавца — тем же
     *  множителем, что и в прямом расчёте, только делением. Получается полная
     *  цена продавца. */
    result = result.dividedBy(this.getVatRebaseMultiplier(vatRateSeller, vatRateBuyer));

    /** Из полной цены продавца — его цена. НДС в ней либо уже сидит, либо был
     *  начислен сверху: во втором случае снимаем. */
    if (isVatIncludedSeller !== true && vatRateSeller !== null) {
      result = result.dividedBy(vatRateSeller.value.dividedBy(100).plus(1));
    }
    else {
      // НДС уже в цене продавца либо продавец не облагается — снимать нечего
    }

    return this.toPrice(priceFullBuyer, result);
  }

  static isCloseTo = NumberUtils.isCloseTo;

  /**
   * Ставка НДС покупателя обязательна: расчёт цены покупателя начинается со
   * сравнения ставок продавца и покупателя, без неё считать нечего.
   *
   * Приходит она из категории товара, а категория у потребности —
   * необязательное поле, поэтому null сюда долетает штатным путём.
   * Вызывающий передавал её с «!», и сравнение падало внутри
   * Numeric.toDecimal сообщением про Decimal, из которого причина не
   * читалась. Здесь ошибка называет саму причину.
   *
   * Возвращает саму ставку, а не void: сузить тип через `asserts` нельзя —
   * TS требует, чтобы имя в цели вызова было объявлено с явной аннотацией
   * типа, а вызов статика через `this.` этого не даёт. Проверка на null
   * поэтому написана сравнением, а не Core.utils.isNullOrUndefined: сравнение
   * сужает тип, предикат — нет.
   *
   * @param vatRateBuyer - ставка НДС покупателя
   */
  private static validateVatRateBuyer(vatRateBuyer: VatRate | null): VatRate {
    if (vatRateBuyer === null || vatRateBuyer === undefined) {
      throw new ApplicationError(
        'Не задана ставка НДС покупателя — цену покупателя рассчитать нельзя',
        0,
        ErrorSeverity.Error,
      );
    }
    else {
      return vatRateBuyer;
    }
  }

  /**
   * Доставка платформой без её стоимости — дефект данных: маршрут не загружен
   * либо расчёт не отработал. Считать в таком виде нельзя: результат вышел бы
   * NaN и молча разъехался по всем ценам.
   */
  private static validateDeliveryPricePerUnit(
    deliveryPricePerUnit: Price | null,
    deliveryType: DeliveryType,
  ): void {
    if (
      deliveryType.id === 'byETP'
      && (Core.utils.isNullOrUndefined(deliveryPricePerUnit) || deliveryPricePerUnit.isEmpty() === true)
    ) {
      throw new ApplicationError(
        'Доставка платформой, но цена доставки не рассчитана',
        0,
        ErrorSeverity.Error,
        {
          deliveryType:         deliveryType.id,
          deliveryPricePerUnit: deliveryPricePerUnit?.toJSON() ?? null,
        },
      );
    }
    else {
      // цена доставки есть либо доставка не платформой — считать можно
    }
  }
}

export default MoneyUtils;